import { z } from "zod";

/**
 * Validates if a string is valid JSON
 * @param jsonString - The string to validate
 * @returns Object with isValid boolean and parsed data or error message
 */
export function validateJSON(jsonString: string): {
  isValid: boolean;
  data?: any;
  error?: string;
} {
  if (!jsonString || jsonString.trim() === "") {
    return { isValid: true, data: {} }; // Empty string is valid (empty object)
  }

  try {
    const parsed = JSON.parse(jsonString);
    return { isValid: true, data: parsed };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : "Invalid JSON format",
    };
  }
}

/**
 * Validates if a JSON object matches a Zod schema
 * @param data - The parsed JSON data to validate
 * @param schema - The Zod schema to validate against
 * @returns Object with isValid boolean and error details if invalid
 */
export function validateAgainstSchema(
  data: any,
  schema: z.ZodSchema
): { isValid: boolean; error?: string; details?: any } {
  try {
    schema.parse(data);
    return { isValid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      // Use 'issues' for Zod v3 compatibility (v4 uses 'errors')
      const issues = (error as any).issues || (error as any).errors || [];
      const errorMessages = issues
        .map((err: any) => `${err.path.join(".")}: ${err.message}`)
        .join(", ");

      return {
        isValid: false,
        error: `Schema validation failed: ${errorMessages}`,
        details: issues,
      };
    }

    return {
      isValid: false,
      error:
        error instanceof Error ? error.message : "Unknown validation error",
    };
  }
}

/**
 * Extracts field information from a Zod schema and converts it to ZodField[] format
 * This is used to initialize the UI with existing schema data
 */
export function extractFieldsFromZodSchema(zodSchema: z.ZodSchema): any[] {
  if (!zodSchema) {
    return [];
  }

  // Check if it's a ZodObject (which has a shape property)
  if (!(zodSchema instanceof z.ZodObject)) {
    return [];
  }

  const shape = zodSchema.shape;
  const fields: any[] = [];

  for (const [key, value] of Object.entries(shape)) {
    const fieldSchema = value as any;

    // Generate a unique ID for the field
    const fieldId = `field-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    let fieldType: string = "string";
    let fileType: string | undefined = undefined;
    let isOptional = false;

    // First check for Zod 4 metadata via .meta() method
    if (typeof fieldSchema.meta === "function") {
      const meta = fieldSchema.meta();
      if (meta?.isFile || meta?.type === "file") {
        fieldType = "file";
        fileType = meta?.fileType;
      }
    }

    // If not a file, detect type using instanceof checks (Zod 4 compliant)
    if (fieldType === "string" && !fileType) {
      // Unwrap optional if present
      let innerSchema = fieldSchema;
      if (fieldSchema instanceof z.ZodOptional) {
        isOptional = true;
        innerSchema = fieldSchema.unwrap();

        // Check metadata on unwrapped schema
        if (typeof innerSchema.meta === "function") {
          const meta = innerSchema.meta();
          if (meta?.isFile || meta?.type === "file") {
            fieldType = "file";
            fileType = meta?.fileType;
          }
        }
        if (innerSchema.__isFile) {
          fieldType = "file";
          fileType = innerSchema.__fileType;
        }
      }

      // Type detection using instanceof (Zod 4 compliant)
      if (fieldType === "string" && !fileType) {
        if (innerSchema instanceof z.ZodString) {
          fieldType = "string";
        } else if (innerSchema instanceof z.ZodNumber) {
          fieldType = "number";
        } else if (innerSchema instanceof z.ZodBoolean) {
          fieldType = "boolean";
        } else if (innerSchema instanceof z.ZodArray) {
          fieldType = "array";
        } else if (innerSchema instanceof z.ZodObject) {
          fieldType = "object";
        }
      }
    }

    fields.push({
      id: fieldId,
      name: key,
      type: fieldType,
      required: !isOptional,
      description: "",
      defaultValue: "",
      children: [],
      fileType: fileType,
      arrayType: fileType || undefined,
    });
  }

  return fields;
}

/**
 * Extracts file type information from a ZodEffects schema
 */

/**
 * Converts a tool's input schema (Zod string or object) to a Zod schema object
 * @param schemaInput - The Zod schema as a string or object
 * @returns Parsed Zod schema or null if invalid
 */
export function parseZodSchema(schemaInput: string | any): z.ZodSchema | null {
  console.log("🔍 Debug - parseZodSchema called with:", schemaInput);
  console.log("🔍 Debug - Schema input type:", typeof schemaInput);

  if (!schemaInput) {
    console.log("🔍 Debug - Empty schema input");
    return null;
  }

  // If it's already a Zod schema object
  if (schemaInput && typeof schemaInput.parse === "function") {
    console.log("🔍 Debug - Input is already a Zod schema");
    return schemaInput;
  }

  // If it's a string, try to parse it as a Zod schema string
  if (typeof schemaInput === "string") {
    if (schemaInput.trim() === "") {
      console.log("🔍 Debug - Empty schema string");
      return null;
    }

    try {
      console.log("🔍 Debug - Creating schema function from string...");
      // Dynamic schema strings are user-authored `z.object(...)` snippets.
      // eslint-disable-next-line no-new-func
      const schemaFunction = new Function("z", `"use strict"; return (${schemaInput});`);
      console.log("🔍 Debug - Executing schema function...");
      const schema = schemaFunction(z);
      console.log("🔍 Debug - Schema function result:", schema);
      console.log("🔍 Debug - Schema type:", typeof schema);
      console.log(
        "🔍 Debug - Schema has parse method:",
        schema && typeof schema.parse === "function"
      );

      // Validate that it's actually a Zod schema
      if (schema && typeof schema.parse === "function") {
        console.log("🔍 Debug - Valid Zod schema created from string");
        return schema;
      }

      console.log("🔍 Debug - Invalid schema - not a Zod schema");
      return null;
    } catch (error) {
      console.error(
        "🔍 Debug - Failed to parse Zod schema from string:",
        error
      );
      return null;
    }
  }

  // If it's an object, try to reconstruct it as a Zod schema
  if (typeof schemaInput === "object") {
    try {
      console.log(
        "🔍 Debug - Attempting to reconstruct Zod schema from object..."
      );
      console.log("🔍 Debug - Schema object structure:", schemaInput);

      // Check if it has the Zod schema structure
      if (schemaInput.def && schemaInput.def.type) {
        console.log("🔍 Debug - Object appears to be a Zod schema with _def");
        if (typeof schemaInput.parse === "function") {
          console.log("🔍 Debug - Object is a valid Zod schema");
          return schemaInput;
        }
      }

      // Try to create a Zod schema from the object structure
      if (
        schemaInput.def &&
        schemaInput.def.type === "object" &&
        schemaInput.def.shape
      ) {
        console.log(
          "🔍 Debug - Attempting to create Zod object schema from def structure"
        );
        const shape = schemaInput.def.shape;
        const zodShape: Record<string, z.ZodTypeAny> = {};

        for (const [key, value] of Object.entries(shape)) {
          console.log(`🔍 Debug - Processing field ${key}:`, value);

          // Try to reconstruct the proper Zod type based on the field definition
          if (value && typeof value === "object" && (value as any).def) {
            const fieldDef = (value as any).def;
            console.log(`🔍 Debug - Field ${key} def:`, fieldDef);

            if (fieldDef.typeName === "ZodString") {
              zodShape[key] = z.string();
            } else if (fieldDef.typeName === "ZodNumber") {
              zodShape[key] = z.number();
            } else if (fieldDef.typeName === "ZodBoolean") {
              zodShape[key] = z.boolean();
            } else if (fieldDef.typeName === "ZodArray") {
              zodShape[key] = z.array(z.any());
            } else if (fieldDef.typeName === "ZodObject") {
              zodShape[key] = z.object({});
            } else {
              // For unknown types, use z.any() but log it
              console.log(
                `🔍 Debug - Unknown field type for ${key}: ${fieldDef.typeName}`
              );
              zodShape[key] = z.any();
            }
          } else {
            // Fallback to z.any() if we can't determine the type
            console.log(
              `🔍 Debug - No field def found for ${key}, using z.any()`
            );
            zodShape[key] = z.any();
          }
        }

        const reconstructedSchema = z.object(zodShape).strict();
        console.log("🔍 Debug - Reconstructed schema:", reconstructedSchema);
        console.log("🔍 Debug - Reconstructed schema shape:", zodShape);
        return reconstructedSchema;
      }

      console.log("🔍 Debug - Could not reconstruct Zod schema from object");
      return null;
    } catch (error) {
      console.error(
        "🔍 Debug - Failed to reconstruct Zod schema from object:",
        error
      );
      return null;
    }
  }

  console.log("🔍 Debug - Unsupported schema input type");
  return null;
}

/**
 * Comprehensive validation for default parameters
 * @param jsonString - The JSON string to validate
 * @param toolSchema - The tool's input schema as a Zod string or object
 * @returns Validation result with detailed error information
 */
export function validateDefaultParameters(
  jsonString: string,
  toolSchema?: string | any
): {
  isValid: boolean;
  jsonValid: boolean;
  schemaValid: boolean;
  error?: string;
  details?: any;
} {
  console.log("🔍 Debug - validateDefaultParameters called with:");
  console.log("  - jsonString:", jsonString);
  console.log("  - toolSchema:", toolSchema);

  // Step 1: Validate JSON format
  const jsonValidation = validateJSON(jsonString);
  console.log("🔍 Debug - JSON validation result:", jsonValidation);

  if (!jsonValidation.isValid) {
    console.log("🔍 Debug - JSON validation failed");
    return {
      isValid: false,
      jsonValid: false,
      schemaValid: false,
      error: `Invalid JSON: ${jsonValidation.error}`,
    };
  }

  // Step 2: If no schema provided, JSON validation is sufficient
  if (!toolSchema) {
    console.log("🔍 Debug - No schema provided, JSON validation sufficient");
    return {
      isValid: true,
      jsonValid: true,
      schemaValid: true,
    };
  }

  // Step 3: Parse and validate against tool schema
  console.log("🔍 Debug - Parsing Zod schema...");
  const parsedSchema = parseZodSchema(toolSchema);
  console.log("🔍 Debug - Parsed schema:", parsedSchema);

  if (!parsedSchema) {
    // Tools may expose a non-Zod schema (e.g. plain JSON Schema) that we
    // can't reconstruct here. JSON well-formedness is the only check we
    // can offer in that case — accept and surface a warning instead of
    // blocking the user with an "Invalid tool schema format" error.
    console.warn(
      "validateDefaultParameters: tool schema is not Zod-parseable; skipping schema validation."
    );
    return {
      isValid: true,
      jsonValid: true,
      schemaValid: true,
    };
  }

  // Step 4: Validate data against schema
  console.log("🔍 Debug - Validating data against schema...");
  const schemaValidation = validateAgainstSchema(
    jsonValidation.data,
    parsedSchema
  );
  console.log("🔍 Debug - Schema validation result:", schemaValidation);

  if (!schemaValidation.isValid) {
    console.log("🔍 Debug - Schema validation failed");
    return {
      isValid: false,
      jsonValid: true,
      schemaValid: false,
      error: schemaValidation.error,
      details: schemaValidation.details,
    };
  }

  console.log("🔍 Debug - All validations passed");
  return {
    isValid: true,
    jsonValid: true,
    schemaValid: true,
  };
}

/**
 * Formats validation error for display
 * @param validationResult - The validation result
 * @returns Formatted error message for UI display
 */
export function formatValidationError(
  validationResult: ReturnType<typeof validateDefaultParameters>
): string {
  if (validationResult.isValid) {
    return "";
  }

  if (!validationResult.jsonValid) {
    return `JSON-Fehler: ${validationResult.error}`;
  }

  if (!validationResult.schemaValid) {
    // Translate common Zod error messages to German
    let germanError = validationResult.error || "Unbekannter Fehler";

    if (germanError.includes("Unrecognized key(s) in object")) {
      const keyMatch = germanError.match(
        /Unrecognized key\(s\) in object: '([^']+)'/
      );
      if (keyMatch) {
        germanError = `Unbekanntes Feld: '${keyMatch[1]}'`;
      }
    } else if (germanError.includes("Required")) {
      germanError = germanError.replace("Required", "Erforderlich");
    } else if (germanError.includes("Expected string, received")) {
      germanError = germanError.replace(
        "Expected string, received",
        "String erwartet, erhalten"
      );
    } else if (germanError.includes("Expected number, received")) {
      germanError = germanError.replace(
        "Expected number, received",
        "Zahl erwartet, erhalten"
      );
    } else if (germanError.includes("Expected boolean, received")) {
      germanError = germanError.replace(
        "Expected boolean, received",
        "Boolean erwartet, erhalten"
      );
    } else if (germanError.includes("Expected array, received")) {
      germanError = germanError.replace(
        "Expected array, received",
        "Array erwartet, erhalten"
      );
    } else if (germanError.includes("Expected object, received")) {
      germanError = germanError.replace(
        "Expected object, received",
        "Objekt erwartet, erhalten"
      );
    }

    return `Schema-Fehler: ${germanError}`;
  }

  return "Unbekannter Validierungsfehler";
}
